import { SVGTextProps, Text, TextProps } from "@react-pdf/renderer";
import {
  proximanovaCondensedFontFamily,
  proximanovaFontFamily,
} from "@app/utils/pdfUtils";

export type PdfFontVariant =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "body1"
  | "body2"
  | "tableHeading"
  | "tableBody1"
  | "tableBody2"
  | "tableBody3"
  | "tableBody4"
  | "barGraph1"
  | "barGraph2"
  | "disclosures"
  | "internalUse"
  | "pageNumber"
  // styles for new reports
  | "v9Regular"
  | "v9Semibold"
  | "v9Bold"
  | "v11Regular"
  | "v11Semibold"
  | "v11Bold"
  | "v12Regular"
  | "v12Semibold"
  | "v12Bold"
  | "v14Regular"
  | "v14Semibold"
  | "v14Bold";

interface PdfFontVariantInfo {
  variant: PdfFontVariant;
  style: {
    fontSize: number;
    fontFamily?: string;
    fontStyle?: "italic" | "normal";
    fontWeight?: "normal" | "medium" | "semibold" | "bold";
    lineHeight: number;
  };
}

const variants: PdfFontVariantInfo[] = [
  {
    variant: "h1",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "semibold",
      fontSize: 40,
      lineHeight: 48.72,
    },
  },
  {
    variant: "h2",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "semibold",
      fontSize: 25,
      lineHeight: 30.45,
    },
  },
  {
    variant: "h3",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "semibold",
      fontSize: 20,
      lineHeight: 24.36,
    },
  },
  {
    variant: "h4",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "semibold",
      fontSize: 17,
      lineHeight: 20.71,
    },
  },
  {
    variant: "h5",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "semibold",
      fontSize: 15,
      lineHeight: 18.27,
    },
  },
  {
    variant: "h6",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "semibold",
      fontSize: 13,
      lineHeight: 15.83,
    },
  },
  {
    variant: "body1",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "normal",
      fontSize: 12,
      lineHeight: 14.62,
    },
  },
  {
    variant: "body2",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "normal", // should be medium
      fontSize: 13,
      lineHeight: 15.83,
    },
  },
  {
    variant: "tableHeading",
    style: {
      fontFamily: proximanovaCondensedFontFamily,
      fontWeight: "bold",
      fontSize: 12,
      lineHeight: 12,
    },
  },
  {
    variant: "tableBody1",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "normal",
      fontSize: 12,
      lineHeight: 12,
    },
  },
  {
    variant: "tableBody2",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "bold",
      fontSize: 12,
      lineHeight: 12,
    },
  },
  {
    variant: "tableBody3",
    style: {
      fontFamily: proximanovaCondensedFontFamily,
      fontWeight: "normal",
      fontSize: 12,
      lineHeight: 12,
    },
  },
  {
    variant: "tableBody4",
    style: {
      fontFamily: proximanovaCondensedFontFamily,
      fontWeight: "bold",
      fontSize: 12,
      lineHeight: 12,
    },
  },
  {
    variant: "barGraph1",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "normal",
      fontSize: 12,
      lineHeight: 14.62,
    },
  },
  {
    variant: "barGraph2",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "normal",
      fontSize: 10,
      lineHeight: 12.18,
    },
  },
  {
    variant: "disclosures",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "normal",
      fontStyle: "italic",
      fontSize: 10,
      lineHeight: 12.18,
    },
  },
  {
    variant: "internalUse",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "normal",
      fontStyle: "italic",
      fontSize: 8,
      lineHeight: 8,
    },
  },
  {
    variant: "pageNumber",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "normal",
      fontSize: 10,
      lineHeight: 12.18,
    },
  },
  // v9
  {
    variant: "v9Regular",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "normal",
      fontSize: 9,
      lineHeight: 9,
    },
  },
  {
    variant: "v9Semibold",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "semibold",
      fontSize: 9,
      lineHeight: 9,
    },
  },
  {
    variant: "v9Bold",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "bold",
      fontSize: 9,
      lineHeight: 9,
    },
  },

  // v11
  {
    variant: "v11Regular",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "normal",
      fontSize: 11,
      lineHeight: 11,
    },
  },
  {
    variant: "v11Semibold",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "semibold",
      fontSize: 11,
      lineHeight: 11,
    },
  },
  {
    variant: "v11Bold",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "bold",
      fontSize: 11,
      lineHeight: 11,
    },
  },

  // v12
  {
    variant: "v12Regular",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "normal",
      fontSize: 12,
      lineHeight: 12,
    },
  },
  {
    variant: "v12Semibold",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "semibold",
      fontSize: 12,
      lineHeight: 12,
    },
  },
  {
    variant: "v12Bold",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "bold",
      fontSize: 12,
      lineHeight: 12,
    },
  },

  // v14
  {
    variant: "v14Regular",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "normal",
      fontSize: 14,
      lineHeight: 14,
    },
  },
  {
    variant: "v14Semibold",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "semibold",
      fontSize: 14,
      lineHeight: 14,
    },
  },
  {
    variant: "v14Bold",
    style: {
      fontFamily: proximanovaFontFamily,
      fontWeight: "bold",
      fontSize: 14,
      lineHeight: 14,
    },
  },
];

type CarTextProps = React.PropsWithChildren<TextProps> & Partial<SVGTextProps>;

interface TextStyle
  extends Omit<
    Extract<CarTextProps["style"], { lineHeight?: string | number }>,
    "lineHeight" | "fontSize"
  > {
  lineHeight?: number;
  fontSize?: number;
}

export interface CarPdfTypographyProps extends Omit<CarTextProps, "style"> {
  variant: PdfFontVariant;
  style?: TextStyle;
  dense?: boolean;
  italic?: boolean;
}

export const CarPdfTypography = ({
  variant,
  style,
  dense,
  italic,
  ...props
}: CarPdfTypographyProps) => {
  const variantStyle = variants.find((i) => i.variant === variant)?.style;

  if (!variantStyle) {
    throw new Error("CarPdfTypography variant style not found");
  }

  const lStyle = style ?? {};

  const lineHeight =
    (lStyle.lineHeight ?? variantStyle.lineHeight) /
    (lStyle.fontSize ?? variantStyle.fontSize);

  return (
    <Text
      style={{
        ...variantStyle,
        fontSize: variantStyle.fontSize,
        fontStyle: italic ? "italic" : (variantStyle.fontStyle ?? "normal"),
        fontWeight: variantStyle.fontWeight ?? "normal",
        letterSpacing: dense ? -0.2 : undefined, // -2% in Figma
        color: "rgba(85, 85, 85, 1)", // softBlack  // "#231F20",
        ...lStyle,
        lineHeight,
      }}
      {...props}
    />
  );
};
